import useSearchPages from '@/@core/constants/search-pages';
import { useViewMode, ViewModeProps } from '@/context/ViewModeContext';
import { useSearchContext } from '@/lib/hooks/searchContext/searchStore';
import { useSite } from '@/lib/hooks/site';
import { usePathname, useRouter } from 'next/navigation';

interface DataProps {
    id: number;
    title: string;
    slug: string;
}

interface NavigationHookProps {
    selectedDistrict: DataProps;
    selectedService: DataProps;
    selectedCompany?: DataProps;
    venueQuery?: string;
    onChangeSelectedData: (type: string, data: any) => void;
}

interface SelectedItem {
    slug?: string;
}

type NavigationCase = 'BOTH_ALL' | 'DISTRICT_ONLY' | 'SERVICE_ONLY' | 'BOTH_SPECIFIC';

export const useNavigation = ({
    selectedDistrict,
    selectedService,
    selectedCompany,
    venueQuery,
    onChangeSelectedData
}: NavigationHookProps) => {
    const pathname = usePathname();
    const router = useRouter();
    const site = useSite();
    const searchPages = useSearchPages();
    const { currentTab, setIsOpen, isOpen }: any = useSearchContext();
    const { setViewMode } = useViewMode();

    const isAllData = (item) => !item.slug || item.slug === "all-data";

    const handleTabZeroNavigation = (selectedDistrict: SelectedItem, selectedService: SelectedItem) => {
        const navigationCase: NavigationCase =
            (isAllData(selectedDistrict) && isAllData(selectedService)) ? 'BOTH_ALL' :
                (!isAllData(selectedDistrict) && isAllData(selectedService)) ? 'DISTRICT_ONLY' :
                    (isAllData(selectedDistrict) && !isAllData(selectedService)) ? 'SERVICE_ONLY' :
                        'BOTH_SPECIFIC';

        const navigationPaths: Record<NavigationCase, string> = {
            'BOTH_ALL': `/${site.setting?.routes?.generalList}`,
            'DISTRICT_ONLY': `/${selectedDistrict.slug}-${site.setting?.routes?.districtSpecificList}`,
            'SERVICE_ONLY': `/${selectedService.slug}`,
            'BOTH_SPECIFIC': `/${selectedDistrict.slug}-${selectedService.slug}`
        };

        const path = navigationPaths[navigationCase];
        router.push(path);
    };

    const handleNavigation = (viewMode: ViewModeProps = "list") => {
        const delayedSetViewMode = () => {
            setViewMode(viewMode);
        };

        if (currentTab === 0 && !selectedDistrict.slug && !selectedService.slug) {
            router.push(`/${site.setting?.routes?.generalList}`)
            delayedSetViewMode();
            return;
        }

        if (currentTab === 0) {
            handleTabZeroNavigation(selectedDistrict, selectedService);
            delayedSetViewMode();
        }

        if (currentTab === 1 && selectedCompany?.slug) {
            router.push(`/${selectedCompany.slug}-indirimleri`);
            setIsOpen(!isOpen);
            delayedSetViewMode();
            return;
        }

        if (currentTab === 2 && venueQuery) {
            const targetRoute = searchPages.includes(pathname)
                ? `${pathname}?venue=${venueQuery}`
                : `/${site.setting?.routes?.generalList}?venue=${venueQuery}`;

            router.push(targetRoute);
            delayedSetViewMode();
        }

        onChangeSelectedData(
            "service",
            selectedService?.id && selectedService?.slug !== "all-data" ? selectedService : {}
        );
        onChangeSelectedData(
            "district",
            selectedDistrict?.id && selectedDistrict?.slug !== "all-data" ? selectedDistrict : {}
        );
    };

    return {
        handleNavigation,
        isOpen,
        setIsOpen
    };
};

